import { useState, useEffect, useMemo } from "react";
import Pagination from "@/components/dashboard/Pagination";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, CalendarOff, Pencil } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BlockedTime {
  id: string;
  type: string;
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  active: boolean;
}

const typeColorMap: Record<string, string> = {
  single_day: "bg-destructive/10 text-destructive border-destructive/20",
  date_range: "bg-destructive/10 text-destructive border-destructive/20",
  time_range: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  datetime_range: "bg-primary/10 text-primary border-primary/20",
  daily_time_range: "bg-violet-500/10 text-violet-600 border-violet-500/20",
};

const typeLabelKeys: Record<string, string> = {
  single_day: "blocked.type_single_day",
  date_range: "blocked.type_date_range",
  time_range: "blocked.type_time_range",
  datetime_range: "blocked.type_datetime_range",
  daily_time_range: "blocked.type_daily_time_range",
};

const BlockedTimesPage = () => {
  const [blocked, setBlocked] = useState<BlockedTime[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<BlockedTime | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const paginatedBlocked = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return blocked.slice(start, start + PAGE_SIZE);
  }, [blocked, page]);
  const { profile } = useAuth();
  const { t } = useLanguage();

  const fetchBlocked = async () => {
    if (!profile?.organisation_id) return;
    const { data } = await supabase
      .from("blocked_times")
      .select("*")
      .eq("organisation_id", profile.organisation_id)
      .order("start_date", { ascending: true });
    setBlocked((data as any) || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchBlocked();
  }, [profile?.organisation_id]);

  const toggleActive = async (item: BlockedTime) => {
    const newActive = !item.active;
    const { error } = await supabase
      .from("blocked_times")
      .update({ active: newActive })
      .eq("id", item.id);
    if (error) {
      toast.error(error.message);
    } else {
      setBlocked((prev) => prev.map((b) => (b.id === item.id ? { ...b, active: newActive } : b)));
      toast.success(newActive ? t("blocked.activated") : t("blocked.deactivated"));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from("blocked_times").delete().eq("id", deleteTarget.id);
    if (error) {
      toast.error(error.message);
    } else {
      setBlocked((prev) => prev.filter((b) => b.id !== deleteTarget.id));
      toast.success(t("blocked.deleted"));
    }
    setDeleteTarget(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-foreground">{t("blocked.title")}</h1>
        <Button asChild>
          <Link to="/dashboard/blocked/new">
            <Plus className="h-4 w-4 mr-2" />
            {t("blocked.block_time")}
          </Link>
        </Button>
      </div>

      {blocked.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <p className="text-muted-foreground mb-4">{t("blocked.no_items")}</p>
          <Button asChild>
            <Link to="/dashboard/blocked/new">
              <Plus className="h-4 w-4 mr-2" />
              {t("blocked.block_time")}
            </Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {paginatedBlocked.map((block) => (
              <div
                key={block.id}
                className={`flex items-center justify-between rounded-xl border border-border bg-card px-5 py-4 shadow-card transition-opacity ${!block.active ? "opacity-50" : ""}`}
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                    <CalendarOff className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${typeColorMap[block.type] || ""}`}>
                        {t(typeLabelKeys[block.type] || block.type)}
                      </span>
                    </div>
                    <p className="text-sm text-foreground">
                      {format(new Date(block.start_date), "MMM d, yyyy")}
                      {block.end_date && ` – ${format(new Date(block.end_date), "MMM d, yyyy")}`}
                      {block.start_time && ` · ${block.start_time.slice(0, 5)} - ${block.end_time?.slice(0, 5)}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={block.active} onCheckedChange={() => toggleActive(block)} />
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground" asChild>
                    <Link to={`/dashboard/blocked/${block.id}/edit`}>
                      <Pencil className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={() => setDeleteTarget(block)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Pagination currentPage={page} totalItems={blocked.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("blocked.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("blocked.delete_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("blocked.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("blocked.delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BlockedTimesPage;